import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isOpenLeaf } from '@d-orca/shared/client/activities';
import type { ICommitRejection } from '@apis/schedule';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import type { ISchedulerTask, SiteId, TaskId } from '@interfaces/IScheduler';
import { useCommitAssignees } from '../../hooks/useCommitAssignees';
import { useSchedulerSite, useSchedulerStale } from '../../hooks/useSchedulerStore';
import { useScheduleDirty } from '../../hooks/useScheduleDirty';
import { useTaskCommitActions } from '../../hooks/useTaskCommitActions';
import { commitGaps } from '../../lib/commitReadiness';
import { AssigneeChips } from './AssigneeChips';
import { CommitRejections } from './CommitRejections';
import { CommitReview } from './CommitReview';
import { ReviewFooter } from './ReviewFooter';
import styles from './CommitReview.module.css';
import manyStyles from './CommitReviewMany.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

// ---------------------------------------------------------------------------
// CommitReviewMany — the list-form review (2026-09-18 commit UX design,
// decision 4): one line per leaf with its readiness, a checkbox to leave a
// leaf out, and Fix on a leaf that is not ready — which opens ITS review in
// place and returns here once it is promised. The set commits all-or-nothing
// through the bulk route; what every leaf shares (who is accountable, the
// safety flag) is asked once, above the list.
// ---------------------------------------------------------------------------

export interface ICommitReviewManyProps {
  siteId: SiteId;
  /** The leaves this review is about — a parent's. */
  taskIds: readonly TaskId[];
  title: string;
  scheduleNumber?: string;
  onClose: () => void;
}

export function CommitReviewMany({ siteId, taskIds, title, scheduleNumber, onClose }: ICommitReviewManyProps) {
  const { t } = useTranslation();
  const site = useSchedulerSite(siteId);
  const dirty = useScheduleDirty(siteId, scheduleNumber);
  const stale = useSchedulerStale(siteId);
  const { commitMany, pending } = useTaskCommitActions({ siteId, taskId: taskIds[0] ?? '', scheduleNumber });
  const { candidates, me } = useCommitAssignees(siteId);

  const [off, setOff] = useState<ReadonlySet<TaskId>>(new Set());
  const [fixing, setFixing] = useState<TaskId | null>(null);
  // Unanswered (`undefined`) until somebody picks: the caller stands in, and
  // keeps standing in once the list names them.
  const [assignee, setAssignee] = useState<string | null | undefined>(undefined);
  const effectiveAssignee = assignee === undefined ? me : assignee;
  const [dangerous, setDangerous] = useState(false);
  // The last attempt's answer: the rows the server refused, or the failure.
  const [rejections, setRejections] = useState<ICommitRejection[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  const byId = new Map((site?.tasks ?? []).map((task) => [task.id, task] as const));
  // Leaves that are still open: a leaf promised through Fix drops out here,
  // and a milestone (never promised) is never listed.
  const leaves = taskIds.map((id) => byId.get(id)).filter((task): task is ISchedulerTask => !!task && isOpenLeaf(task));

  if (fixing) {
    return (
      <CommitReview siteId={siteId} taskId={fixing} scheduleNumber={scheduleNumber} onClose={() => setFixing(null)} />
    );
  }

  const chosen = leaves.filter((leaf) => !off.has(leaf.id));
  const notReady = chosen.filter((leaf) => commitGaps(leaf).length > 0);
  const canCommit = chosen.length > 0 && notReady.length === 0 && !stale && !dirty && !pending;
  const reason = stale
    ? t('scheduler.stale.banner')
    : dirty
      ? t('scheduler.commit.saveFirst')
      : chosen.length === 0
        ? t('scheduler.commit.nothingChosen')
        : notReady.length > 0
          ? t('scheduler.commit.notReadyCount', { count: notReady.length })
          : t('scheduler.commit.allSet');

  const submit = async () => {
    const outcome = await commitMany(
      chosen.map((leaf) => ({ taskId: leaf.id, dangerousActivity: leaf.kind === 'field' && dangerous, assignee: effectiveAssignee })),
    );
    // As in the single review: a refusal lists its rows, a failure stays in
    // the footer, and only a made promise closes the review.
    setRejections(outcome.rejections);
    setFailure(outcome.error ?? null);
    if (outcome.ok) onClose();
  };

  return (
    <div className={styles.root} data-testid="commit-review-many">
      <ScrollArea className={styles.body} viewportClassName={styles.bodyContent}>
        <h3 className={manyStyles.title}>{title}</h3>
        <p className={styles.intro}>{t('scheduler.commit.reviewManyIntro')}</p>

        <div className={manyStyles.shared}>
          <span className={styles.label}>{t('scheduler.commit.row.assignee')}</span>
          <AssigneeChips candidates={candidates} value={effectiveAssignee} onPick={setAssignee} />
          <span className={styles.label}>{t('scheduler.commit.row.safety')}</span>
          <label className={styles.check}>
            <Checkbox checked={dangerous} onCheckedChange={setDangerous} ariaLabel={t('scheduler.commit.dangerous')} />
            <span>{t('scheduler.commit.dangerousAll')}</span>
          </label>
        </div>

        <ul className={manyStyles.list}>
          {leaves.map((leaf) => {
            const gaps = commitGaps(leaf);
            const included = !off.has(leaf.id);
            return (
              <li key={leaf.id} className={`${manyStyles.leaf} ${included ? '' : manyStyles.off}`} data-testid={`leaf-${leaf.id}`}>
                <Checkbox
                  checked={included}
                  onCheckedChange={(next) => {
                    const nextOff = new Set(off);
                    if (next) nextOff.delete(leaf.id);
                    else nextOff.add(leaf.id);
                    setOff(nextOff);
                  }}
                  ariaLabel={t('scheduler.commit.include', { name: leaf.name })}
                />
                <span className={manyStyles.name}>
                  {leaf.name}
                  <small className={gaps.length > 0 ? manyStyles.gap : manyStyles.sub}>
                    {leaf.kind === 'administrative'
                      ? t('scheduler.commit.kind.administrative')
                      : gaps.length > 0
                        ? t('scheduler.commit.missing', { what: gaps.map((gap) => t(`scheduler.commit.gap.${gap}`)).join(', ') })
                        : <>{leaf.profession && <ProfessionPill profession={leaf.profession} compact />} · {t('scheduler.panel.locations.assignedCount', { total: leaf.locations?.length ?? 0 })}</>}
                  </small>
                </span>
                {!included ? (
                  <span className={styles.muted}>{t('scheduler.commit.leftOut')}</span>
                ) : gaps.length > 0 ? (
                  <button type="button" className={styles.linkBtn} onClick={() => setFixing(leaf.id)} data-testid={`fix-${leaf.id}`}>
                    {t('scheduler.commit.fix')}
                  </button>
                ) : (
                  <span className={styles.good}>✓ {t('scheduler.commit.readyShort')}</span>
                )}
              </li>
            );
          })}
        </ul>
        {leaves.length === 0 && <p className={styles.muted}>{t('scheduler.commit.allCommitted')}</p>}
        <CommitRejections rejections={rejections} nameOf={(id) => byId.get(id)?.name} />
      </ScrollArea>

      <ReviewFooter
        reason={reason}
        error={failure}
        canCommit={canCommit}
        pending={pending}
        confirmLabel={t('scheduler.commit.confirmMany', { count: chosen.length, total: leaves.length })}
        confirmTestId="commit-many-confirm"
        onCancel={onClose}
        onConfirm={() => void submit()}
      />
    </div>
  );
}
